<script>
import Layout from '@layouts/main'

import Loading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/vue-loading.css'

import VueApexCharts from 'vue-apexcharts'
import serviceContaPagar from '@/src/services/conta-pagar-receber'
import moment from 'moment'

moment.locale('pt-br')

export default {
  components: { Layout, Loading, apexchart: VueApexCharts },
  data() {
    return {
      currentMonth: '01',
      filtersSidebarOpen: false,
      cashFlow: [],
      cashFlowChart: {
        pagamentos: 0,
        recebimentos: 0,
      },
      isLoading: false,
      monthOptions: [
        { value: '01', text: 'Janeiro' },
        { value: '02', text: 'Fevereiro' },
        { value: '03', text: 'Marco' },
        { value: '04', text: 'Abril' },
        { value: '05', text: 'Maio' },
        { value: '06', text: 'Junho' },
        { value: '07', text: 'Julho' },
        { value: '08', text: 'Agosto' },
        { value: '09', text: 'Setembro' },
        { value: '10', text: 'Outubro' },
        { value: '11', text: 'Novembro' },
        { value: '12', text: 'Dezembro' },
      ],
      cashFlowFields: [
        { key: 'data', label: 'Data' },
        { key: 'recebimentos', label: 'Recebimentos', class: 'text-right' },
        { key: 'pagamentos', label: 'Pagamentos', class: 'text-right' },
        { key: 'saldo', label: 'Saldo', class: 'text-right' },
      ],
      columnsExcel: [
        { label: 'Data', field: 'data_formatada' },
        { label: 'Recebimentos', field: 'recebimentos_formatado' },
        { label: 'Pagamentos', field: 'pagamentos_formatado' },
        { label: 'Saldo', field: 'saldo_formatado' },
      ],
      pieSeries: [],
      pieOptions: {
        colors: ['#0b9a7d', '#db5b57'],
        chart: {
          type: 'donut',
          toolbar: { show: false },
        },
        labels: ['Recebimentos', 'Pagamentos'],
        legend: {
          position: 'bottom',
        },
        plotOptions: {
          pie: {
            donut: {
              size: '68%',
            },
          },
        },
        dataLabels: {
          enabled: false,
        },
      },
      trendSeries: [],
      trendOptions: {
        chart: {
          type: 'line',
          height: 360,
          stacked: false,
          toolbar: { show: false },
        },
        stroke: {
          width: [0, 0, 3],
          curve: 'smooth',
        },
        plotOptions: {
          bar: {
            columnWidth: '42%',
            borderRadius: 6,
          },
        },
        colors: ['#0b9a7d', '#db5b57', '#11493f'],
        dataLabels: {
          enabled: false,
        },
        labels: [],
        xaxis: {
          type: 'category',
        },
        yaxis: [
          {
            labels: {
              formatter: (value) => this.formatCurrency(value),
            },
          },
          {
            opposite: true,
            labels: {
              formatter: (value) => this.formatCurrency(value),
            },
          },
        ],
        legend: {
          position: 'top',
        },
        tooltip: {
          shared: true,
          intersect: false,
          y: {
            formatter: (value) => this.formatCurrency(value),
          },
        },
      },
    }
  },
  computed: {
    rows() {
      return this.cashFlow.length
    },
    selectedMonthLabel() {
      const selected = this.monthOptions.find(
        (item) => item.value === this.currentMonth
      )
      return selected ? selected.text : ''
    },
    totalRecebimentos() {
      const chartValue = this.getChartValue([
        'recebimentos',
        'entradas',
        'total_recebimentos',
        'totalRecebimentos',
        'total_entradas',
        'totalEntradas',
      ])
      if (chartValue !== null) return chartValue
      return this.cashFlow.reduce(
        (total, item) => total + this.parseCurrency(item.recebimentos),
        0
      )
    },
    totalPagamentos() {
      const chartValue = this.getChartValue([
        'pagamentos',
        'saidas',
        'saídas',
        'total_pagamentos',
        'totalPagamentos',
        'total_saidas',
        'totalSaidas',
      ])
      if (chartValue !== null) return chartValue
      return this.cashFlow.reduce(
        (total, item) => total + this.parseCurrency(item.pagamentos),
        0
      )
    },
    saldoPeriodo() {
      return this.totalRecebimentos - this.totalPagamentos
    },
    saldoInicial() {
      const chartValue = this.getChartValue([
        'saldo_inicial',
        'saldoInicial',
        'saldoInicialPeriodo',
        'saldo_anterior',
        'saldoAnterior',
      ])
      return chartValue !== null ? chartValue : 0
    },
    saldoFinal() {
      return this.saldoInicial + this.saldoPeriodo
    },
    cashFlowOrdered() {
      return [...this.cashFlow].sort((a, b) =>
        moment.utc(a.data).diff(moment.utc(b.data))
      )
    },
    cashFlowWithBalance() {
      let saldoAcumulado = this.saldoInicial
      return this.cashFlowOrdered.map((item) => {
        const recebimentos = this.parseCurrency(item.recebimentos)
        const pagamentos = this.parseCurrency(item.pagamentos)
        saldoAcumulado += recebimentos - pagamentos
        return {
          ...item,
          saldoCalculado: saldoAcumulado,
        }
      })
    },
    chartMonths() {
      return this.cashFlowWithBalance.map((item) => ({
        label: this.formatDate(item.data),
        entradas: this.parseCurrency(item.recebimentos),
        saidas: this.parseCurrency(item.pagamentos),
        saldo: item.saldoCalculado,
      }))
    },
    monthlyChartSeries() {
      return [
        {
          name: 'Entradas',
          type: 'column',
          data: this.chartMonths.map((item) => Math.round(item.entradas * 100) / 100),
        },
        {
          name: 'Saídas',
          type: 'column',
          data: this.chartMonths.map((item) => Math.round(item.saidas * 100) / 100),
        },
        {
          name: 'Saldo',
          type: 'line',
          data: this.chartMonths.map((item) => Math.round(item.saldo * 100) / 100),
        },
      ]
    },
    monthlyChartOptions() {
      return {
        chart: {
          fontFamily: 'inherit',
          stacked: false,
          toolbar: { show: false },
        },
        colors: ['#006b55', '#b45d59', '#172033'],
        dataLabels: {
          enabled: false,
        },
        grid: {
          borderColor: '#edf1f6',
          strokeDashArray: 4,
          xaxis: { lines: { show: false } },
        },
        labels: this.chartMonths.map((item) => item.label),
        legend: {
          show: false,
        },
        plotOptions: {
          bar: {
            borderRadius: 4,
            columnWidth: '42%',
          },
        },
        stroke: {
          curve: 'smooth',
          width: [0, 0, 3],
        },
        tooltip: {
          shared: true,
          intersect: false,
          y: {
            formatter: (value) => this.formatCurrency(value),
          },
        },
        xaxis: {
          axisBorder: { show: false },
          axisTicks: { show: false },
          labels: {
            style: {
              colors: '#536074',
              fontSize: '12px',
              fontWeight: 700,
            },
          },
        },
        yaxis: {
          labels: {
            formatter: (value) =>
              new Intl.NumberFormat('pt-BR', {
                notation: 'compact',
                compactDisplay: 'short',
              }).format(value),
            style: {
              colors: '#7b8796',
              fontSize: '11px',
            },
          },
        },
      }
    },
    monthlyHighlights() {
      const months = this.chartMonths
      if (!months.length) {
        return {
          maiorEntrada: null,
          maiorSaida: null,
        }
      }

      return {
        maiorEntrada: [...months].sort((a, b) => b.entradas - a.entradas)[0],
        maiorSaida: [...months].sort((a, b) => b.saidas - a.saidas)[0],
      }
    },
    proximosLancamentos() {
      return this.cashFlowWithBalance.slice(0, 3).map((item) => ({
        ...item,
        titulo: this.formatDate(item.data),
        subtitulo: 'Fluxo diário',
        valor: Math.abs(
          this.parseCurrency(item.recebimentos) - this.parseCurrency(item.pagamentos)
        ),
        tipo:
          this.parseCurrency(item.recebimentos) >= this.parseCurrency(item.pagamentos)
            ? 'entrada'
            : 'saida',
      }))
    },
    movimentosRecentes() {
      return this.cashFlowWithBalance
    },
    melhorDia() {
      if (!this.cashFlow.length) {
        return null
      }

      return [...this.cashFlow]
        .map((item) => ({
          ...item,
          saldoNumerico: this.parseSaldoValue(item.saldo),
        }))
        .sort((a, b) => b.saldoNumerico - a.saldoNumerico)[0]
    },
    piorDia() {
      if (!this.cashFlow.length) {
        return null
      }

      return [...this.cashFlow]
        .map((item) => ({
          ...item,
          saldoNumerico: this.parseSaldoValue(item.saldo),
        }))
        .sort((a, b) => a.saldoNumerico - b.saldoNumerico)[0]
    },
    excelData() {
      return this.cashFlow.map((item) => ({
        data_formatada: this.formatDate(item.data),
        recebimentos_formatado: this.formatCurrency(
          this.parseCurrency(item.recebimentos)
        ),
        pagamentos_formatado: this.formatCurrency(
          this.parseCurrency(item.pagamentos)
        ),
        saldo_formatado: this.formatCurrency(this.parseSaldoValue(item.saldo)),
      }))
    },
  },
  mounted() {
    this.currentMonth = moment().utcOffset('-03:00').format('MM')
    this.listando(this.currentMonth)
  },
  methods: {
    listando(month) {
      const token = this.$store.state.auth.currentUser.token
      const selectedMonth = Number(month)
      this.currentMonth = String(selectedMonth).padStart(2, '0')
      this.isLoading = true

      serviceContaPagar
        .getCashFlow(token, selectedMonth)
        .then((resposta) => {
          const data = resposta.data || {}
          this.cashFlow = Array.isArray(data.contas) ? data.contas : []
          this.cashFlowChart = data.chart || {
            pagamentos: 0,
            recebimentos: 0,
          }
          this.pieSeries = [
            this.parseCurrency(this.cashFlowChart.recebimentos),
            this.parseCurrency(this.cashFlowChart.pagamentos),
          ]
          this.updateTrendChart()
        })
        .catch((error) => {
          this.cashFlow = []
          this.cashFlowChart = {
            pagamentos: 0,
            recebimentos: 0,
          }
          this.pieSeries = [0, 0]
          this.updateTrendChart()
          this.$bvToast.toast(this.apiErrorMessage(error), {
            variant: 'danger',
            solid: false,
            noCloseButton: true,
            toaster: 'b-toaster-top-center',
          })
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    apiErrorMessage(error) {
      const data = error && error.response ? error.response.data : null
      if (data && data.aviso && data.aviso.mensagem) return data.aviso.mensagem
      if (data && data.mensagem) return data.mensagem
      if (data && Array.isArray(data.message)) return data.message.join(', ')
      if (data && data.message) return data.message
      return error && error.message
        ? error.message
        : 'Nao foi possivel carregar o fluxo de caixa.'
    },
    updateTrendChart() {
      this.trendSeries = [
        {
          name: 'Recebimentos',
          type: 'column',
          data: this.cashFlowWithBalance.map((item) =>
            this.parseCurrency(item.recebimentos)
          ),
        },
        {
          name: 'Pagamentos',
          type: 'column',
          data: this.cashFlowWithBalance.map((item) =>
            this.parseCurrency(item.pagamentos)
          ),
        },
        {
          name: 'Saldo acumulado',
          type: 'line',
          data: this.cashFlowWithBalance.map((item) => item.saldoCalculado),
        },
      ]

      this.trendOptions = {
        ...this.trendOptions,
        labels: this.cashFlowWithBalance.map((item) => this.formatDate(item.data)),
      }
    },
    getChartValue(keys) {
      const source = this.cashFlowChart || {}
      const key = keys.find(
        (item) =>
          source[item] !== undefined && source[item] !== null && source[item] !== ''
      )
      return key ? this.parseCurrency(source[key]) : null
    },
    formatDate(date) {
      return moment.utc(date).format('DD/MM/YYYY')
    },
    parseCurrency(value) {
      if (value === null || value === undefined || value === '') {
        return 0
      }

      if (typeof value === 'number') {
        return value
      }

      const cleaned = String(value)
        .replace(/R\$\s?/g, '')
        .replace(/\./g, '')
        .replace(',', '.')
        .trim()

      const parsed = Number(cleaned)
      return Number.isFinite(parsed) ? parsed : 0
    },
    parseSaldoValue(saldo) {
      if (saldo && typeof saldo === 'object' && saldo.valor !== undefined) {
        return this.parseCurrency(saldo.valor)
      }

      return this.parseCurrency(saldo)
    },
    formatCurrency(value) {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(this.parseCurrency(value))
    },
    formatSaldo(item) {
      if (item.saldoCalculado !== undefined) {
        return this.formatCurrency(item.saldoCalculado)
      }
      if (item.saldo && typeof item.saldo === 'object' && item.saldo.valor) {
        return item.saldo.valor
      }
      return this.formatCurrency(this.parseSaldoValue(item.saldo))
    },
    saldoStyle(item) {
      if (item.saldo && typeof item.saldo === 'object' && item.saldo.cor) {
        return { color: item.saldo.cor }
      }
      return {}
    },
    saldoClass(item) {
      const saldo =
        item.saldoCalculado !== undefined
          ? item.saldoCalculado
          : this.parseSaldoValue(item.saldo)
      if (saldo < 0) {
        return 'cash-badge is-negative'
      }

      if (saldo === 0) {
        return 'cash-badge is-neutral'
      }

      return 'cash-badge is-positive'
    },
    monthSubtitle() {
      return `Consolidado de ${this.selectedMonthLabel.toLowerCase()}`
    },
    applyCashFlowFilters() {
      this.listando(this.currentMonth)
      this.filtersSidebarOpen = false
    },
    resetCashFlowFilters() {
      this.currentMonth = moment().utcOffset('-03:00').format('MM')
      this.listando(this.currentMonth)
      this.filtersSidebarOpen = false
    },
    movementDescription(item, index) {
      const descriptions = [
        'Venda Software SaaS',
        'Fornecedor Hardware Ltda',
        'Prestação de Serviço Consultoria',
        'Energia Elétrica - Copel',
      ]
      return descriptions[index % descriptions.length]
    },
    movementCategory(index) {
      return ['Licenciamento', 'Equipamentos', 'Consultoria', 'Infraestrutura'][
        index % 4
      ]
    },
    movementType(item) {
      return this.parseCurrency(item.recebimentos) >= this.parseCurrency(item.pagamentos)
        ? 'entrada'
        : 'saida'
    },
    movementValue(item) {
      const type = this.movementType(item)
      return type === 'entrada'
        ? this.parseCurrency(item.recebimentos)
        : this.parseCurrency(item.pagamentos)
    },
    movementStatus(item) {
      const saldo = this.parseSaldoValue(item.saldo)
      if (saldo < 0) return 'Pendente'
      return 'Conciliado'
    },
  },
}
</script>

<template>
  <Layout>
    <div class="bloco cash-page">
      <div class="cabecalho">
        <div class="row">
          <div class="col-md-7 mb-2">
            <div class="titulo">
              <i class="uil uil-chart-line"></i>
              Fluxo de Caixa
            </div>
            <div class="subTitulo">
              Controle detalhado de entradas, saídas e projeção de saldo.
            </div>
          </div>
          <div class="col-md-5">
            <div class="receivable-page-actions">
              <div class="cash-table-filters cash-header-filters">
                <button
                  class="btn btn-light cash-filter-button"
                  type="button"
                  @click="filtersSidebarOpen = true"
                >
                  <i class="uil uil-filter"></i>
                  Filtros
                </button>
              </div>
              <b-dropdown
                class="cash-new-entry-menu"
                menu-class="cash-new-entry-popup"
                no-caret
                right
                variant="webreceita"
              >
                <template #button-content>
                  <i class="uil uil-plus"></i>
                  Novo Lançamento
                </template>
                <b-dropdown-item :to="{ name: 'Pagar' }">
                  <i class="uil uil-arrow-up-right"></i>
                  Contas a pagar
                </b-dropdown-item>
                <b-dropdown-item :to="{ name: 'Receber' }">
                  <i class="uil uil-arrow-down-left"></i>
                  Contas a receber
                </b-dropdown-item>
              </b-dropdown>
              <vue-excel-xlsx
                ref="btnexport"
                style="display: none"
                :data="excelData"
                :columns="columnsExcel"
                :filename="'fluxo_caixa'"
                :sheetname="'fluxo_caixa'"
              />
            </div>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-md-12">
          <div class="borda"></div>
        </div>
      </div>

      <b-sidebar
        id="cash-filters-sidebar"
        v-model="filtersSidebarOpen"
        title="Filtros"
        right
        shadow
        backdrop
        width="390px"
        body-class="cash-filters-sidebar-body"
      >
        <b-form class="cash-filter-form" @submit.prevent="applyCashFlowFilters">
          <b-form-group label="Mês">
            <b-form-select
              v-model="currentMonth"
              :options="monthOptions"
            />
          </b-form-group>
          <div class="cash-filter-sidebar-actions">
            <button
              class="btn btn-light"
              type="button"
              @click="resetCashFlowFilters"
            >
              Limpar filtros
            </button>
            <button class="btn btn-webreceita" type="submit">
              <i class="uil uil-search"></i>
              Aplicar filtros
            </button>
          </div>
        </b-form>
      </b-sidebar>

      <div class="receivable-dashboard cash-dashboard">
        <div class="receivable-kpi-grid cash-kpi-grid">
          <article class="receivable-kpi cash-kpi-card">
            <span class="cash-kpi-icon is-neutral"><i class="uil uil-arrow-right"></i></span>
            <div>
              <span class="cash-kpi-label">Saldo Inicial</span>
              <strong class="cash-kpi-value">{{ formatCurrency(saldoInicial) }}</strong>
            </div>
          </article>
          <article class="receivable-kpi cash-kpi-card is-positive">
            <span class="cash-kpi-icon is-positive"><i class="uil uil-arrow-down"></i></span>
            <div>
              <span class="cash-kpi-label">Entradas</span>
              <strong class="cash-kpi-value is-positive">{{ formatCurrency(totalRecebimentos) }}</strong>
            </div>
          </article>
          <article class="receivable-kpi cash-kpi-card is-danger">
            <span class="cash-kpi-icon is-negative"><i class="uil uil-arrow-up"></i></span>
            <div>
              <span class="cash-kpi-label">Saídas</span>
              <strong class="cash-kpi-value is-negative">{{ formatCurrency(totalPagamentos) }}</strong>
            </div>
          </article>
          <article class="receivable-kpi cash-kpi-card">
            <span class="cash-kpi-icon is-balance"><i class="uil uil-university"></i></span>
            <div>
              <span class="cash-kpi-label">Saldo Final</span>
              <strong class="cash-kpi-value">{{ formatCurrency(saldoFinal) }}</strong>
            </div>
          </article>
        </div>

      <section class="cash-panel cash-analysis-panel">
        <div class="cash-panel-head">
          <h2><i class="uil uil-chart-bar"></i> Análise Mensal</h2>
          <div class="cash-chart-legend">
            <span><i class="is-positive"></i> Entradas</span>
            <span><i class="is-negative"></i> Saídas</span>
            <span><i class="is-balance"></i> Saldo</span>
            <button type="button">{{ selectedMonthLabel }}</button>
          </div>
        </div>
        <div class="cash-analysis-body">
          <div class="cash-chart-wrap">
            <apexchart
              type="line"
              height="300"
              :options="monthlyChartOptions"
              :series="monthlyChartSeries"
            />
          </div>
          <aside class="cash-analysis-summary">
            <article>
              <span>Maior entrada</span>
              <strong>
                {{ monthlyHighlights.maiorEntrada ? formatCurrency(monthlyHighlights.maiorEntrada.entradas) : formatCurrency(0) }}
              </strong>
              <small>
                {{ monthlyHighlights.maiorEntrada ? monthlyHighlights.maiorEntrada.label : '-' }}
              </small>
            </article>
            <article>
              <span>Maior saída</span>
              <strong class="is-negative">
                {{ monthlyHighlights.maiorSaida ? formatCurrency(monthlyHighlights.maiorSaida.saidas) : formatCurrency(0) }}
              </strong>
              <small>
                {{ monthlyHighlights.maiorSaida ? monthlyHighlights.maiorSaida.label : '-' }}
              </small>
            </article>
            <article>
              <span>Resultado líquido</span>
              <strong :class="{ 'is-negative': saldoPeriodo < 0 }">
                {{ formatCurrency(saldoPeriodo) }}
              </strong>
              <small>{{ selectedMonthLabel }}</small>
            </article>
          </aside>
          <div v-if="!cashFlow.length" class="cash-chart-empty">
            Nenhum movimento encontrado para montar a análise.
          </div>
        </div>
      </section>

      <section class="cash-panel cash-projections">
        <div class="cash-panel-head compact">
          <h2><i class="uil uil-exchange"></i> Próximos Lançamentos</h2>
        </div>
        <div class="cash-projection-grid">
          <article
            v-for="item in proximosLancamentos"
            :key="`${item.data}-${item.titulo}`"
            :class="['cash-projection-card', `is-${item.tipo}`]"
          >
            <span><i :class="item.tipo === 'entrada' ? 'uil uil-arrow-up-right' : 'uil uil-arrow-down-left'"></i></span>
            <div>
              <strong>{{ item.titulo }}</strong>
              <small>{{ item.subtitulo }}</small>
            </div>
            <b>{{ item.tipo === 'entrada' ? '+' : '-' }} {{ formatCurrency(item.valor) }}</b>
          </article>
        </div>
      </section>

      <section class="receivable-list-card cash-table-card">
        <div class="cash-panel-head compact">
          <h2><i class="uil uil-list-ul"></i> Fluxo diário</h2>
        </div>

        <div class="cash-table-wrap">
          <table class="cash-movements-table">
            <thead>
              <tr>
                <th>Data</th>
                <th class="text-right">Pagamentos</th>
                <th class="text-right">Recebimentos</th>
                <th class="text-right">Saldo</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in movimentosRecentes" :key="`${item.data}-${index}`">
                <td>{{ formatDate(item.data) }}</td>
                <td class="text-right">
                  <strong class="cash-table-value is-saida">
                    {{ item.pagamentos || formatCurrency(0) }}
                  </strong>
                </td>
                <td class="text-right">
                  <strong class="cash-table-value is-entrada">
                    {{ item.recebimentos || formatCurrency(0) }}
                  </strong>
                </td>
                <td class="text-right">
                  <strong class="cash-table-value" :style="saldoStyle(item)">
                    {{ formatSaldo(item) }}
                  </strong>
                </td>
              </tr>
              <tr v-if="!movimentosRecentes.length">
                <td colspan="4" class="cash-empty-row">
                  Nenhum fluxo de caixa encontrado para {{ selectedMonthLabel.toLowerCase() }}.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="cash-table-footer">
          <span>Mostrando {{ movimentosRecentes.length }} de {{ rows }} dias</span>
        </div>
      </section>

      <loading :active.sync="isLoading" color="#008e75" :can-cancel="false" />
      </div>
    </div>
  </Layout>
</template>

<style lang="scss">
.bloco.cash-page {
  background: #fff;
  border: 1px solid #e3e8ef;
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.05);
  margin-top: 24px;
  padding: 32px;
  position: relative;
}

.cash-page .titulo {
  align-items: center;
  color: #008e75;
  display: flex;
  font-size: 25px;
  font-weight: 800;
  gap: 8px;
  line-height: 1.2;
  margin-bottom: 8px;
}

.cash-page .subTitulo {
  color: #536074;
  font-size: 14px;
}

.cash-page .borda {
  border-bottom: 1px solid #edeff4;
  margin: 15px 0 18px;
}

.receivable-page-actions {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: flex-end;
}

.receivable-page-actions .btn {
  align-items: center;
  border-radius: 5px;
  display: inline-flex;
  font-weight: 700;
  gap: 7px;
  height: 42px;
  justify-content: center;
  margin: 0;
  min-width: 130px;
  padding: 0 18px;
}

.receivable-page-actions .btn-light {
  background: #fff;
  border: 1px solid #d8dee8;
  color: #172033;
}

.btn-webreceita {
  background: #008e75;
  border-color: #008e75;
  color: #fff;
}

.cash-new-entry-menu .dropdown-toggle {
  align-items: center;
  display: inline-flex;
  gap: 7px;
}

.cash-new-entry-popup {
  border: 1px solid #dfe6ef;
  border-radius: 7px;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.12);
  min-width: 210px;
  padding: 6px;
}

.cash-new-entry-popup .dropdown-item {
  align-items: center;
  border-radius: 5px;
  color: #172033;
  display: flex;
  font-weight: 700;
  gap: 8px;
  padding: 10px 12px;
}

.cash-new-entry-popup .dropdown-item i {
  color: #008e75;
  font-size: 16px;
}

.cash-new-entry-popup .dropdown-item:hover,
.cash-new-entry-popup .dropdown-item:focus {
  background: #effaf6;
  color: #008e75;
}

.receivable-dashboard {
  background: #f5f7fb;
  display: grid;
  gap: 18px;
  margin: 0 -32px -32px;
  padding: 18px 32px 32px;
}

.receivable-kpi-grid {
  display: grid;
  gap: 18px;
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.receivable-kpi,
.cash-panel,
.receivable-list-card {
  background: #fff;
  border: 1px solid #dfe6ef;
  border-radius: 7px;
}

.cash-table-filters button,
.cash-panel-head.compact > button {
  align-items: center;
  border-radius: 5px;
  display: inline-flex;
  font-weight: 700;
  gap: 7px;
  height: 38px;
  justify-content: center;
  margin: 0;
  padding: 0 16px;
}

.cash-filter-form {
  display: grid;
  gap: 14px;
  padding: 4px 8px 18px;
}

.cash-filters-sidebar-body {
  padding: 18px 20px 84px;
}

.cash-filter-form .form-group {
  margin: 0;
}

.cash-filter-form label {
  color: #172033;
  font-size: 13px;
  font-weight: 800;
}

.cash-filter-form .form-control,
.cash-filter-form .custom-select {
  border: 1px solid #d8dee8;
  border-radius: 5px;
  color: #536074;
  font-weight: 700;
  height: 42px;
  min-height: 42px;
}

.cash-filter-form .form-control:focus,
.cash-filter-form .custom-select:focus {
  border-color: #008e75;
  box-shadow: 0 0 0 3px rgba(0, 142, 117, 0.12);
}

.cash-filter-sidebar-actions {
  align-items: center;
  background: #fff;
  border-top: 1px solid #e7edf4;
  bottom: 0;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  left: 0;
  padding: 14px 20px;
  position: absolute;
  right: 0;
  width: 100%;
}

.cash-filter-sidebar-actions .btn {
  align-items: center;
  border-radius: 5px;
  border-width: 1px;
  display: inline-flex;
  font-weight: 800;
  gap: 7px;
  height: 42px;
  justify-content: center;
  margin: 0;
  min-width: 138px;
  padding: 0 18px;
  white-space: nowrap;
}

.cash-filter-sidebar-actions .btn-light {
  background: #fff;
  border-color: #d8dee8;
  color: #172033;
}

.cash-filter-sidebar-actions .btn-webreceita {
  min-width: 178px;
}

.cash-kpi-grid {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.cash-kpi-card {
  align-items: center;
  display: flex;
  gap: 14px;
  min-height: 82px;
  padding: 16px 18px;
}

.cash-kpi-icon {
  align-items: center;
  border-radius: 8px;
  display: inline-flex;
  flex: 0 0 42px;
  font-size: 22px;
  height: 42px;
  justify-content: center;
  width: 42px;
}

.cash-kpi-icon.is-neutral { background: #eef3fb; color: #536074; }
.cash-kpi-icon.is-positive { background: #e3f7f1; color: #008e75; }
.cash-kpi-icon.is-negative { background: #ffe9ed; color: #dc3545; }
.cash-kpi-icon.is-balance { background: #eef1f2; color: #536074; }

.cash-kpi-label {
  color: #536074;
  display: block;
  font-size: 12px;
  font-weight: 800;
}

.cash-kpi-value {
  color: #172033;
  display: block;
  font-size: 19px;
  font-weight: 800;
  line-height: 1.2;
}

.cash-kpi-value.is-positive { color: #008e75; }
.cash-kpi-value.is-negative { color: #dc3545; }

.cash-panel {
  padding: 22px 26px;
}

.cash-table-card {
  overflow: hidden;
  padding: 0;
}

.cash-table-card .cash-panel-head {
  padding: 20px 24px 0;
}

.cash-panel-head {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 18px;
}

.cash-panel-head.compact {
  margin-bottom: 16px;
}

.cash-panel-head h2 {
  align-items: center;
  color: #172033;
  display: flex;
  font-size: 18px;
  font-weight: 800;
  gap: 8px;
  margin: 0;
}

.cash-panel-head h2 i {
  color: #008e75;
}

.cash-chart-legend {
  align-items: center;
  display: flex;
  gap: 18px;
}

.cash-chart-legend span {
  align-items: center;
  color: #536074;
  display: inline-flex;
  font-size: 12px;
  gap: 6px;
}

.cash-chart-legend i {
  border-radius: 50%;
  display: inline-block;
  height: 8px;
  width: 8px;
}

.cash-chart-legend i.is-positive { background: #006b55; }
.cash-chart-legend i.is-negative { background: #b45d59; }
.cash-chart-legend i.is-balance { background: #172033; }

.cash-chart-legend button,
.cash-panel-head.compact > button {
  background: #f1f5fb;
  border: 0;
  border-radius: 5px;
  color: #536074;
  font-weight: 700;
  height: 30px;
  padding: 0 12px;
}

.cash-analysis-panel {
  min-height: 360px;
}

.cash-analysis-body {
  align-items: stretch;
  display: grid;
  gap: 18px;
  grid-template-columns: minmax(0, 1fr) 220px;
  position: relative;
}

.cash-chart-wrap {
  min-height: 300px;
}

.cash-analysis-summary {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.cash-analysis-summary article {
  background: #f7fafc;
  border: 1px solid #e5ebf3;
  border-radius: 7px;
  display: grid;
  gap: 5px;
  min-height: 82px;
  padding: 13px 14px;
}

.cash-analysis-summary span {
  color: #536074;
  font-size: 12px;
  font-weight: 800;
}

.cash-analysis-summary strong {
  color: #008e75;
  font-size: 18px;
  font-weight: 800;
  line-height: 1.2;
}

.cash-analysis-summary strong.is-negative {
  color: #dc3545;
}

.cash-analysis-summary small {
  color: #7b8796;
  font-size: 12px;
}

.cash-chart-empty {
  align-items: center;
  background: rgba(255, 255, 255, 0.82);
  bottom: 0;
  color: #647084;
  display: flex;
  font-weight: 700;
  justify-content: center;
  left: 0;
  position: absolute;
  right: 238px;
  text-align: center;
  top: 0;
}

.cash-projection-grid {
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.cash-projection-card {
  align-items: center;
  background: #f4f7ff;
  border-radius: 7px;
  display: grid;
  gap: 10px;
  grid-template-columns: auto 1fr auto;
  padding: 14px 16px;
}

.cash-projection-card > span {
  align-items: center;
  border-radius: 6px;
  display: inline-flex;
  height: 30px;
  justify-content: center;
  width: 30px;
}

.cash-projection-card.is-entrada > span { background: #e3f7f1; color: #008e75; }
.cash-projection-card.is-saida > span { background: #ffe9ed; color: #dc3545; }
.cash-projection-card strong, .cash-projection-card small { display: block; }
.cash-projection-card strong { color: #172033; font-weight: 800; }
.cash-projection-card small { color: #647084; font-size: 11px; }
.cash-projection-card b { color: #dc3545; }
.cash-projection-card.is-entrada b { color: #008e75; }

.cash-table-filters {
  align-items: center;
  display: flex;
  gap: 10px;
}

.cash-header-filters {
  flex: 0 0 auto;
  justify-content: flex-end;
}

.cash-table-filters .custom-select {
  background-color: #f4f7fb;
  border: 0;
  height: 36px;
  min-width: 150px;
}

.cash-header-filters .custom-select {
  min-width: 160px;
}

.cash-table-filters button {
  background: #fff;
  border: 1px solid #d8dee8;
  color: #172033;
  min-width: 118px;
}

.cash-table-filters .cash-filter-button {
  height: 42px;
  margin: 0;
}

.cash-table-wrap {
  border-top: 1px solid #dfe6ef;
  overflow-x: auto;
}

.cash-movements-table {
  border-collapse: collapse;
  min-width: 840px;
  width: 100%;
}

.cash-movements-table th {
  background: #eef3fb;
  color: #536074;
  font-size: 12px;
  font-weight: 800;
  padding: 14px 16px;
  text-align: left;
}

.cash-movements-table td {
  border-top: 1px solid #e7edf4;
  color: #172033;
  padding: 15px 16px;
}

.cash-category {
  background: #e8f1ff;
  border-radius: 4px;
  color: #47739e;
  font-size: 11px;
  font-weight: 700;
  padding: 3px 8px;
}

.cash-type-dot {
  align-items: center;
  border-radius: 50%;
  color: #fff;
  display: inline-flex;
  height: 18px;
  justify-content: center;
  width: 18px;
}

.cash-type-dot.is-entrada { background: #006b55; }
.cash-type-dot.is-saida { background: #c41230; }
.cash-table-value.is-entrada { color: #008e75; }
.cash-table-value.is-saida { color: #dc3545; }

.cash-status {
  color: #536074;
  font-size: 12px;
  font-weight: 700;
}

.cash-status::before {
  border-radius: 50%;
  content: "";
  display: inline-block;
  height: 5px;
  margin-right: 5px;
  vertical-align: middle;
  width: 5px;
}

.cash-status.is-ok::before { background: #008e75; }
.cash-status.is-pending::before { background: #9aa3af; }

.cash-more {
  background: transparent;
  border: 0;
  color: #172033;
  font-size: 18px;
}

.cash-empty-row {
  color: #647084;
  text-align: center;
}

.cash-table-footer {
  align-items: center;
  background: #eef3fb;
  border-top: 1px solid #dfe6ef;
  color: #647084;
  display: flex;
  justify-content: space-between;
  padding: 14px 18px;
}

.cash-table-footer div {
  align-items: center;
  display: flex;
  gap: 8px;
}

.cash-table-footer button {
  background: #fff;
  border: 1px solid #d8dee8;
  border-radius: 5px;
  color: #536074;
  height: 32px;
  min-width: 32px;
}

.cash-table-footer button.active {
  background: #008e75;
  border-color: #008e75;
  color: #fff;
}

@media (max-width: 1199px) {
  .cash-kpi-grid,
  .cash-projection-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .cash-analysis-body {
    grid-template-columns: 1fr;
  }

  .cash-analysis-summary {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .cash-chart-empty {
    right: 0;
  }
}

@media (max-width: 767px) {
  .receivable-page-actions,
  .cash-panel-head,
  .cash-table-footer {
    align-items: stretch;
    flex-direction: column;
    gap: 12px;
  }

  .cash-table-filters {
    flex-direction: column;
  }

  .receivable-page-actions .btn,
  .cash-table-filters .custom-select,
  .cash-table-filters button {
    width: 100%;
  }

  .cash-kpi-grid,
  .cash-projection-grid {
    grid-template-columns: 1fr;
  }

  .cash-analysis-summary {
    grid-template-columns: 1fr;
  }
}
</style>
